import { useCallback, useEffect, useState } from 'react';
import type { Label, LabelStatus } from '@trivio/contracts';
import { listLabels } from '../../lib/api-client';

export interface LabelFilters {
  status?: LabelStatus;
  external_ref?: string;
}

export interface UseLabelsResult {
  labels: Label[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  refetch: () => void;
}

/**
 * `GET /v1/labels` list + cursor pagination (`limit` fixed at 50, TTP-244
 * AC). Re-fetches from scratch whenever `apiKey` or the filters change —
 * `apiKey` itself changes whenever the dashboard session key is re-ensured
 * under a different org (`DashboardSessionKeyContext.tsx`), which is what
 * makes "trocar de org zera caches" fall out for free, with no separate
 * cache-invalidation call (TTP-244 brief: no react-query/SWR, `useEffect` +
 * `useState` per feature is the established pattern, `orgId`/its proxy as a
 * dependency is enough at this scale).
 */
export function useLabels(apiKey: string | null, filters: LabelFilters): UseLabelsResult {
  const [labels, setLabels] = useState<Label[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const status = filters.status;
  const externalRef = filters.external_ref;

  useEffect(() => {
    if (!apiKey) {
      setLabels([]);
      setCursor(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    listLabels(apiKey, { ...(status ? { status } : {}), ...(externalRef ? { external_ref: externalRef } : {}), limit: 50 })
      .then((result) => {
        if (cancelled) return;
        setLabels(result.data);
        setCursor(result.next_cursor);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Não foi possível carregar as etiquetas.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [apiKey, status, externalRef, reloadToken]);

  const loadMore = useCallback(() => {
    if (!apiKey || !cursor || loadingMore) {
      return;
    }
    setLoadingMore(true);
    listLabels(apiKey, { ...(status ? { status } : {}), ...(externalRef ? { external_ref: externalRef } : {}), cursor, limit: 50 })
      .then((result) => {
        setLabels((prev) => [...prev, ...result.data]);
        setCursor(result.next_cursor);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Não foi possível carregar mais etiquetas.');
      })
      .finally(() => setLoadingMore(false));
  }, [apiKey, cursor, loadingMore, status, externalRef]);

  const refetch = useCallback(() => setReloadToken((t) => t + 1), []);

  return { labels, loading, loadingMore, error, hasMore: cursor !== null, loadMore, refetch };
}
